import { applyDraft, createDefaultDraft, normalizeDraft } from '../domain/draft.js';
import { cloneValue, deepFreeze } from '../domain/model-utils.js';

const owns = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
const sameOrder = (left, right) => left.length === right.length && left.every((id, index) => id === right[index]);

function targetIndex(currentIndex, length, target) {
  if (target === 'up') return Math.max(0, currentIndex - 1);
  if (target === 'down') return Math.min(length - 1, currentIndex + 1);
  if (target === 'first') return 0;
  if (target === 'last') return Math.max(0, length - 1);
  return Math.max(0, Math.min(length - 1, Number(target) || 0));
}

function movedOrder(ids, id, target) {
  const from = ids.indexOf(id);
  if (from < 0 || ids.length < 2) return ids;
  const to = targetIndex(from, ids.length, target);
  if (from === to) return ids;
  const next = [...ids];
  next.splice(from, 1);
  next.splice(to, 0, id);
  return next;
}

function selectionSummary(document) {
  const chapters = document.chapters || [];
  const allItems = chapters.flatMap(chapter => chapter.items || []);
  const effectiveItems = chapters
    .filter(chapter => chapter.selected)
    .flatMap(chapter => chapter.items || [])
    .filter(item => item.selected);
  return {
    selectedChapters: chapters.filter(chapter => chapter.selected).length,
    chapterCount: chapters.length,
    selectedItems: effectiveItems.length,
    itemCount: allItems.length
  };
}

/**
 * @param {Record<string, any> | {document: Record<string, any>, draft?: unknown}} documentOrOptions
 * @param {unknown} [draftValue]
 */
export function createDeliverablesStore(documentOrOptions, draftValue) {
  const options = documentOrOptions?.document ? documentOrOptions : { document: documentOrOptions, draft: draftValue };
  const baseDocument = deepFreeze(cloneValue(options.document));
  const baseChapters = new Map(baseDocument.chapters.map(chapter => [chapter.id, chapter]));
  const baseItems = new Map(baseDocument.chapters.flatMap(chapter => chapter.items.map(item => [item.id, item])));
  const listeners = new Set();
  let revision = 0;

  function makeState(draft) {
    const document = applyDraft(baseDocument, draft);
    return deepFreeze({
      projectId: baseDocument.project.id,
      revision,
      draft,
      document,
      selection: selectionSummary(document)
    });
  }

  let state = makeState(normalizeDraft(options.draft || createDefaultDraft(baseDocument.project.id), baseDocument.project.id));

  function commit(nextDraft) {
    const normalized = normalizeDraft(nextDraft, baseDocument.project.id);
    if (JSON.stringify(normalized) === JSON.stringify(state.draft)) return state;
    revision += 1;
    state = makeState(normalized);
    listeners.forEach(listener => listener(state));
    return state;
  }

  function changeSelection(group, id, selected, fallback) {
    const currentMap = state.draft.selections[group];
    const nextMap = { ...currentMap };
    if (selected === fallback) delete nextMap[id];
    else nextMap[id] = selected;
    return commit({
      ...state.draft,
      selections: { ...state.draft.selections, [group]: nextMap }
    });
  }

  function patchOverride(group, id, patch, allowedFields, base) {
    if (!base) return state;
    const current = state.draft.overrides[group][id] || {};
    const nextPatch = { ...current };
    for (const field of allowedFields) {
      if (!owns(patch, field) || typeof patch[field] !== 'string') continue;
      if (patch[field] === (base[field] || '')) delete nextPatch[field];
      else nextPatch[field] = patch[field];
    }
    const groupOverrides = { ...state.draft.overrides[group] };
    if (Object.keys(nextPatch).length) groupOverrides[id] = nextPatch;
    else delete groupOverrides[id];
    return commit({
      ...state.draft,
      overrides: { ...state.draft.overrides, [group]: groupOverrides }
    });
  }

  function findChapterForItem(itemId) {
    return state.document.chapters.find(chapter => chapter.items.some(item => item.id === itemId));
  }

  const api = {
    getState: () => state,

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    toggleChapter(chapterId, selected) {
      const current = state.document.chapters.find(chapter => chapter.id === chapterId);
      const base = baseChapters.get(chapterId);
      if (!current || !base) return state;
      return changeSelection('chapters', chapterId, selected ?? !current.selected, base.selected);
    },

    toggleItem(chapterOrItemId, itemOrSelected, maybeSelected) {
      const hasChapter = typeof itemOrSelected === 'string';
      const itemId = hasChapter ? itemOrSelected : chapterOrItemId;
      const selected = hasChapter ? maybeSelected : itemOrSelected;
      const current = findChapterForItem(itemId)?.items.find(item => item.id === itemId);
      const base = baseItems.get(itemId);
      if (!current || !base) return state;
      return changeSelection('items', itemId, selected ?? !current.selected, base.selected);
    },

    moveChapter(chapterId, target) {
      const currentIds = state.document.chapters.map(chapter => chapter.id);
      const nextOrder = movedOrder(currentIds, chapterId, target);
      if (sameOrder(currentIds, nextOrder)) return state;
      const baseOrder = baseDocument.chapters.map(chapter => chapter.id);
      return commit({ ...state.draft, chapterOrder: sameOrder(nextOrder, baseOrder) ? [] : nextOrder });
    },

    moveChapterBy(chapterId, offset) {
      const ids = state.document.chapters.map(chapter => chapter.id);
      return api.moveChapter(chapterId, ids.indexOf(chapterId) + Number(offset || 0));
    },

    moveItem(chapterOrItemId, itemOrTarget, maybeTarget) {
      const explicitChapter = maybeTarget !== undefined;
      const itemId = explicitChapter ? itemOrTarget : chapterOrItemId;
      const target = explicitChapter ? maybeTarget : itemOrTarget;
      const chapter = explicitChapter
        ? state.document.chapters.find(row => row.id === chapterOrItemId)
        : findChapterForItem(itemId);
      if (!chapter) return state;
      const currentIds = chapter.items.map(item => item.id);
      const nextOrder = movedOrder(currentIds, itemId, target);
      if (sameOrder(currentIds, nextOrder)) return state;
      const baseOrder = (baseChapters.get(chapter.id)?.items || []).map(item => item.id);
      const itemOrder = { ...state.draft.itemOrder };
      if (sameOrder(nextOrder, baseOrder)) delete itemOrder[chapter.id];
      else itemOrder[chapter.id] = nextOrder;
      return commit({ ...state.draft, itemOrder });
    },

    moveItemBy(chapterId, itemId, offset) {
      const chapter = state.document.chapters.find(row => row.id === chapterId);
      const ids = chapter?.items.map(item => item.id) || [];
      return api.moveItem(chapterId, itemId, ids.indexOf(itemId) + Number(offset || 0));
    },

    updateChapter(chapterId, patch) {
      return patchOverride('chapters', chapterId, patch || {}, ['title', 'note'], baseChapters.get(chapterId));
    },

    updateItem(chapterOrItemId, itemOrPatch, maybePatch) {
      const itemId = maybePatch ? itemOrPatch : chapterOrItemId;
      const patch = maybePatch || itemOrPatch;
      return patchOverride('items', itemId, patch || {}, ['title', 'caption', 'note'], baseItems.get(itemId));
    },

    updateChapterTitle: (id, title) => api.updateChapter(id, { title }),
    updateChapterNote: (id, note) => api.updateChapter(id, { note }),
    updateItemTitle: (id, title) => api.updateItem(id, { title }),
    updateItemCaption: (id, caption) => api.updateItem(id, { caption }),
    updateItemNote: (id, note) => api.updateItem(id, { note }),

    selectAll() {
      const chapters = {};
      const items = {};
      for (const chapter of baseDocument.chapters) {
        const selected = chapter.items.length > 0;
        if (selected !== chapter.selected) chapters[chapter.id] = selected;
        for (const item of chapter.items) if (item.selected !== true) items[item.id] = true;
      }
      return commit({ ...state.draft, selections: { chapters, items } });
    },

    selectNone() {
      const chapters = {};
      const items = {};
      for (const chapter of baseDocument.chapters) {
        if (chapter.selected !== false) chapters[chapter.id] = false;
        for (const item of chapter.items) if (item.selected !== false) items[item.id] = false;
      }
      return commit({ ...state.draft, selections: { chapters, items } });
    },

    replaceDraft: draft => commit(draft),

    reset() {
      return commit(createDefaultDraft(baseDocument.project.id));
    }
  };

  return api;
}
